/**
 * Portal data-access helpers (Phase G, impl v10).
 *
 * Thin supabase-js wrappers for the authenticated subscriber portal. No ORM
 * (ARCH-001). RLS enforces tenant isolation (ARCH-002 / migration 0007); these
 * helpers never pass a subscriber_id filter on read because the RLS policy
 * already scopes every row to `is_owner(subscriber_id)`. Writes set only the
 * columns the subscriber is allowed to write per the migration 0007 policies.
 *
 * The portal renders for a subscriber whose `subscribers.status = 'active'`
 * and whose `onboarding_state.onboarding_status = 'eric_reviewed'`. The guard
 * (route-guards usage in portal.tsx + lib/portal-guard.ts) enforces entry; the
 * helpers here assume an authenticated session is present.
 *
 * Coordination with Phase F: the onboarding-complete check reads
 * `onboarding_state` (READ ONLY). Phase F owns writes to that table during the
 * onboarding flow; the portal only reads `onboarding_status`,
 * `eric_reviewed_at`, `welcome_view_block`, `welcome_view_dismissed_at`.
 */

import { supabase } from './supabase.js';
import type {
  ContextDocSectionsPayload,
  DeliverableContent,
  EmailFrequency,
  PlaybookEntry,
  Source,
  Subscriber,
  Task,
  TaskStatus,
  TaskType,
  YourNote,
} from '@studio/api-schemas';

/**
 * The subscriber row for the signed-in user. RLS `subscribers_self_select`
 * returns at most one row (own, not soft-deleted).
 */
export async function fetchOwnSubscriber(): Promise<Subscriber | null> {
  const { data, error } = await supabase
    .from('subscribers')
    .select(
      'id, user_id, email, tier, status, stripe_customer_id, stripe_subscription_id, email_frequency, paused_at, paused_until, canceled_at, created_at, updated_at, deleted_at',
    )
    .maybeSingle();
  if (error) throw error;
  return (data as Subscriber | null) ?? null;
}

/**
 * Onboarding state for the signed-in subscriber. READ ONLY (Phase F owns
 * writes). Used by the portal entry guard and the transient welcome view.
 */
export interface PortalOnboardingState {
  onboarding_status: 'pending' | 'in_progress' | 'submitted' | 'eric_reviewed';
  eric_reviewed_at: string | null;
  welcome_view_block: string | null;
  welcome_view_dismissed_at: string | null;
}

export async function fetchOwnOnboardingState(): Promise<PortalOnboardingState | null> {
  const { data, error } = await supabase
    .from('onboarding_state')
    .select(
      'onboarding_status, eric_reviewed_at, welcome_view_block, welcome_view_dismissed_at',
    )
    .maybeSingle();
  if (error) throw error;
  return (data as PortalOnboardingState | null) ?? null;
}

/**
 * Onboarding token + status for the signed-in subscriber. READ ONLY (Phase F
 * owns writes). Used by the bare `/onboarding` token-resolution route so a
 * mid-onboarding subscriber can be forwarded to their tokenized flow without a
 * sign-out + magic-link round trip. The subscriber owns their onboarding row
 * (`onboarding_state_subscriber_select`, migration 0007), so `onboarding_token`
 * is readable under RLS for the owner.
 */
export interface PortalOnboardingResume {
  onboarding_token: string;
  onboarding_status: 'pending' | 'in_progress' | 'submitted' | 'eric_reviewed';
}

export async function fetchOwnOnboardingResume(): Promise<PortalOnboardingResume | null> {
  const { data, error } = await supabase
    .from('onboarding_state')
    .select('onboarding_token, onboarding_status')
    .maybeSingle();
  if (error) throw error;
  return (data as PortalOnboardingResume | null) ?? null;
}

/**
 * Mark the welcome view dismissed. The subscriber owns the
 * `onboarding_state` UPDATE policy (`onboarding_state_subscriber_update`), so
 * this writes only `welcome_view_dismissed_at`. We do not touch any Phase F
 * column.
 */
export async function dismissWelcomeView(): Promise<void> {
  const { error } = await supabase
    .from('onboarding_state')
    .update({ welcome_view_dismissed_at: new Date().toISOString() })
    .is('welcome_view_dismissed_at', null);
  if (error) throw error;
}

/**
 * All non-deleted tasks for the signed-in subscriber. RLS
 * `tasks_subscriber_select` scopes to ownership. Ordered so the kanban can
 * bucket them: backlog by `order_in_backlog`, the rest by recency.
 */
export async function fetchOwnTasks(): Promise<Task[]> {
  const { data, error } = await supabase
    .from('tasks')
    .select(
      'id, subscriber_id, type, status, brief, intake_mode, coaching_depth, parent_task_id, order_in_backlog, pull_time, due_at, delivered_at, approved_at, created_at, updated_at, deleted_at',
    )
    .order('order_in_backlog', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as Task[]) ?? [];
}

export async function fetchTaskById(taskId: string): Promise<Task | null> {
  const { data, error } = await supabase
    .from('tasks')
    .select(
      'id, subscriber_id, type, status, brief, intake_mode, coaching_depth, parent_task_id, order_in_backlog, pull_time, due_at, delivered_at, approved_at, created_at, updated_at, deleted_at',
    )
    .eq('id', taskId)
    .maybeSingle();
  if (error) throw error;
  return (data as Task | null) ?? null;
}

/**
 * The deliverable for a delivered/approved task, read through the
 * `public_deliverables` view per ARCH-005 §6/§7. The view excludes operator
 * scratchpad and subscriber-identity columns; it only surfaces rows whose
 * task is `delivered` or `approved`.
 */
export interface PublicDeliverableView {
  task_id: string;
  task_type: TaskType;
  content: DeliverableContent;
  content_schema_version: number;
  format: string;
  video_url: string | null;
  video_provider: string | null;
  published_at: string | null;
  meta_skeleton: string | null;
  meta_voice_decisions: string | null;
  meta_what_left_out: string | null;
  meta_do_yourself_next_time: string | null;
  meta_sources_line: string | null;
  subscriber_tier: 'studio' | 'pro';
}

export async function fetchPublicDeliverable(
  taskId: string,
): Promise<PublicDeliverableView | null> {
  const { data, error } = await supabase
    .from('public_deliverables')
    .select(
      'task_id, task_type, content, content_schema_version, format, video_url, video_provider, published_at, meta_skeleton, meta_voice_decisions, meta_what_left_out, meta_do_yourself_next_time, meta_sources_line, subscriber_tier',
    )
    .eq('task_id', taskId)
    .maybeSingle();
  if (error) throw error;
  return (data as PublicDeliverableView | null) ?? null;
}

/**
 * Structured source citations for a task. RLS `sources_subscriber_select`
 * scopes to ownership-of-parent-task.
 */
export async function fetchTaskSources(taskId: string): Promise<Source[]> {
  const { data, error } = await supabase
    .from('sources')
    .select('type, ref, label')
    .eq('task_id', taskId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data as Source[]) ?? [];
}

/**
 * The single context doc for the signed-in subscriber (read-only on the
 * subscriber side per RLS; the subscriber UPDATE policy exists but the portal
 * context page is a read-only render per PR-23 / T-044).
 */
export interface PortalContextDoc {
  sections: ContextDocSectionsPayload;
  sections_schema_version: number;
  updated_at: string;
}

export async function fetchOwnContextDoc(): Promise<PortalContextDoc | null> {
  const { data, error } = await supabase
    .from('context_docs')
    .select('sections, sections_schema_version, updated_at')
    .maybeSingle();
  if (error) throw error;
  return (data as PortalContextDoc | null) ?? null;
}

/**
 * "Your notes" feed, newest first. RLS `your_notes_subscriber_select`.
 */
export async function fetchOwnYourNotes(): Promise<YourNote[]> {
  const { data, error } = await supabase
    .from('your_notes')
    .select(
      'id, subscriber_id, body, promoted_to_context_at, promoted_section, created_at, deleted_at',
    )
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as YourNote[]) ?? [];
}

/**
 * Append a "Your notes" entry. Append-only by V1 invariant; the subscriber
 * INSERT policy (`your_notes_subscriber_insert`) checks `is_owner`. We must
 * supply subscriber_id because the WITH CHECK clause evaluates it; resolve it
 * from the caller's own subscriber row.
 */
export async function appendYourNote(
  subscriberId: string,
  body: string,
): Promise<YourNote> {
  const { data, error } = await supabase
    .from('your_notes')
    .insert({ subscriber_id: subscriberId, body })
    .select(
      'id, subscriber_id, body, promoted_to_context_at, promoted_section, created_at, deleted_at',
    )
    .single();
  if (error) throw error;
  return data as YourNote;
}

/**
 * Playbook entries for the signed-in subscriber. Optional full-text search
 * over `content` and tag filtering. The DB has a GIN tsvector index on
 * `to_tsvector('english', content)` and a GIN index on `tags`; we use
 * `textSearch` and the `contains` operator so PostgREST drives those indexes.
 */
export async function fetchOwnPlaybookEntries(opts?: {
  search?: string;
  tag?: string;
}): Promise<PlaybookEntry[]> {
  let query = supabase
    .from('playbook_entries')
    .select(
      'id, subscriber_id, task_id, task_type, tags, content, created_at, updated_at, deleted_at',
    )
    .order('created_at', { ascending: false });

  const search = opts?.search?.trim();
  if (search) {
    query = query.textSearch('content', search, {
      type: 'websearch',
      config: 'english',
    });
  }
  if (opts?.tag) {
    query = query.contains('tags', [opts.tag]);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data as PlaybookEntry[]) ?? [];
}

/**
 * Notification feed for the signed-in user. RLS `notifications_self_select`
 * scopes to `recipient_user_id = auth.uid()`.
 */
export interface PortalNotification {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export async function fetchOwnNotifications(): Promise<PortalNotification[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('id, type, payload, read_at, created_at')
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data as PortalNotification[]) ?? [];
}

export async function countUnreadNotifications(): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .is('read_at', null);
  if (error) throw error;
  return count ?? 0;
}

export async function markNotificationRead(id: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id)
    .is('read_at', null);
  if (error) throw error;
}

/**
 * Update the subscriber's email-frequency preference. The subscribers table
 * has no subscriber-side UPDATE policy (tier/status/email are
 * webhook/operator-only per ARCH-001 §7 + migration 0007). email_frequency is
 * a subscriber preference, not a billing field, but there is no narrow RLS
 * UPDATE policy for it in V1. We route this through the settings API endpoint
 * (server-side, service-role) instead of a direct table write.
 *
 * This helper calls POST /api/settings/email-frequency. The endpoint is owned
 * by the server surface (consume only); see T-046 IMPL-NOTES for the
 * coordination flag if the endpoint does not yet exist.
 */
export async function updateEmailFrequency(
  accessToken: string,
  emailFrequency: EmailFrequency,
): Promise<{ ok: true } | { ok: false; status: number; message: string }> {
  try {
    const res = await fetch('/api/settings/email-frequency', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ email_frequency: emailFrequency }),
    });
    if (res.status === 200) return { ok: true };
    return {
      ok: false,
      status: res.status,
      message:
        res.status === 404
          ? 'The email-frequency endpoint is not available yet.'
          : 'Could not update your email preference. Try again in a moment.',
    };
  } catch {
    return {
      ok: false,
      status: 0,
      message: 'Could not reach the settings service. Try again in a moment.',
    };
  }
}

/**
 * GDPR account deletion (PR-25b, DATA-16, DES-07b; ARCH-014). Calls
 * POST /api/subscriber/account/delete with the explicit typed confirmation.
 *
 * Outcomes map the server flow (ARCH-014 §1) to a UI-facing union:
 *   - deleted          the wipe ran; account erased, Stripe canceled
 *   - already_deleted  idempotent: the account was already gone (no re-send)
 *   - migration_pending the wipe RPC (migration 0018) is not deployed yet —
 *                       the backup was emailed but NOTHING was erased. Mirrors
 *                       lib/onboarding.ts: degrade honestly, never fake a
 *                       "deleted".
 *   - backup_failed    the backup bundle could not be emailed -> nothing was
 *                       wiped (ARCH-014 §1.2 gate). Safe to retry.
 *   - dry_run          email is in dry-run mode -> nothing sent, nothing
 *                       wiped (the gate cannot be satisfied without a real
 *                       send).
 *   - error            anything else.
 *
 * The endpoint is server-owned (consume only); the calling contract degrades
 * honestly when the endpoint or the RPC is absent rather than implying the
 * account was deleted.
 */
export type DeleteAccountOutcome =
  | { kind: 'deleted'; stripe: string }
  | { kind: 'already_deleted' }
  | { kind: 'migration_pending'; message: string }
  | { kind: 'backup_failed'; message: string }
  | { kind: 'dry_run'; message: string }
  | { kind: 'error'; message: string };

export async function deleteAccount(
  accessToken: string,
): Promise<DeleteAccountOutcome> {
  let res: Response;
  try {
    res = await fetch('/api/subscriber/account/delete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ confirm: 'DELETE' }),
    });
  } catch {
    return {
      kind: 'error',
      message: 'Could not reach the deletion service. Try again in a moment.',
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  const obj = (json ?? {}) as Record<string, unknown>;

  if (res.status === 404) {
    // Endpoint not deployed yet. Degrade honestly: nothing was deleted.
    return {
      kind: 'migration_pending',
      message:
        'Account deletion is not available yet. Nothing was erased. Please try again later or email eric@yesplease.studio.',
    };
  }

  if (res.status === 200 && obj.ok === true) {
    if (obj.outcome === 'already_deleted') return { kind: 'already_deleted' };
    return {
      kind: 'deleted',
      stripe: typeof obj.stripe === 'string' ? obj.stripe : 'unknown',
    };
  }

  const detail =
    typeof obj.detail === 'string' ? obj.detail : 'Something went wrong.';
  if (obj.error === 'migration_pending') {
    return { kind: 'migration_pending', message: detail };
  }
  if (obj.error === 'backup_send_failed') {
    return { kind: 'backup_failed', message: detail };
  }
  if (obj.error === 'backup_dry_run') {
    return { kind: 'dry_run', message: detail };
  }
  if (obj.error === 'confirmation_required') {
    return { kind: 'error', message: detail };
  }
  return { kind: 'error', message: detail };
}

/**
 * Tier-aware cycle copy used in several portal surfaces.
 */
export function cycleLabelForTier(tier: 'studio' | 'pro'): string {
  return tier === 'pro' ? '48-hour cycle' : '72-hour cycle';
}

/**
 * Active-slot count enforced visibly per PR-13 (1 for Studio, 2 for Pro).
 */
export function activeSlotsForTier(tier: 'studio' | 'pro'): number {
  return tier === 'pro' ? 2 : 1;
}

/**
 * Kanban bucket for a task status. Backlog statuses (`backlog`,
 * `ready_to_pull`, `intake_shape_check`) sit in the Backlog column; the
 * in-flight statuses are Active; `delivered`/`approved` are Delivered.
 * `split` parents are hidden (their children carry the work).
 */
export type KanbanColumn = 'backlog' | 'active' | 'delivered' | 'hidden';

export function kanbanColumnForStatus(status: TaskStatus): KanbanColumn {
  switch (status) {
    case 'intake_shape_check':
    case 'backlog':
    case 'ready_to_pull':
      return 'backlog';
    case 'pulled':
    case 'in_progress':
    case 'agent_draft':
    case 'eric_reviewing':
    case 'ready_to_deliver':
      return 'active';
    case 'delivered':
    case 'approved':
      return 'delivered';
    case 'split':
      return 'hidden';
    default:
      return 'hidden';
  }
}
